"use client";

import {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { GONDOLA_DEFAULT_DESCRIPTION } from "@/lib/metadataConstants";

type PageMetadata = {
  title: string;
  description?: string;
  pathname?: string;
  originUrl?: string;
};

export const defaultMetadata: PageMetadata = {
  title: "Gondola",
  description: GONDOLA_DEFAULT_DESCRIPTION,
  pathname: "/",
  originUrl: typeof window !== "undefined" ? window.location.origin : "",
};

type HeaderContextType = {
  setPageMetadata?: Dispatch<SetStateAction<PageMetadata>>;
};

const HeaderContext = createContext<HeaderContextType>({});

/**
 * Provides setPageMetadata for client-side title updates (e.g. hotel details, search).
 * Meta tags (og, twitter, description) come only from Next.js metadata (layout + generateMetadata).
 * We only sync title to document.title when a child has called setPageMetadata, so pages that rely
 * on generateMetadata (e.g. hotel details) keep the server-rendered title until the client opts in.
 */
export default function HeaderProvider({ children }: PropsWithChildren) {
  const [pageMetadata, setPageMetadata] =
    useState<PageMetadata>(defaultMetadata);
  /**
   * Once any page calls setPageMetadata, this stays true for the lifetime of the provider (session).
   * We never reset it so that we don't overwrite server-rendered titles on full navigations to pages
   * that use generateMetadata (e.g. hotel details). Tradeoff: navigating from a client-title page
   * to a page that never calls setPageMetadata can leave the previous title in the tab until that
   * page mounts and (if it does) sets a new one.
   */
  const hasClientOverride = useRef(false);

  const setPageMetadataWithFlag = useCallback(
    (action: SetStateAction<PageMetadata>) => {
      hasClientOverride.current = true;
      setPageMetadata(action);
    },
    []
  );

  useEffect(() => {
    if (hasClientOverride.current) {
      document.title = pageMetadata.title;
    }
  }, [pageMetadata.title]);

  return (
    <HeaderContext.Provider
      value={{ setPageMetadata: setPageMetadataWithFlag }}
    >
      {children}
    </HeaderContext.Provider>
  );
}

export const useHeader = () => {
  const { setPageMetadata } = useContext(HeaderContext);

  return { setPageMetadata };
};
